/* CampaignEmailTemplatesScreen.js — Full Email Template Builder & Campaign Manager
   Tabs: Templates, Editor, Sent, Contacts, Analytics, Scheduler, Design
   Integrated with MarketingPromotionScreen & campaigns API */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SafeAreaView, View, Text, TouchableOpacity, ScrollView, StatusBar,
  Alert, ActivityIndicator, Dimensions, RefreshControl, Modal, TextInput,
  Image, KeyboardAvoidingView, Platform, Switch, Animated,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Sharing from 'expo-sharing';
import * as Print from 'expo-print';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

const { width } = Dimensions.get('window');
const P = '#0e4e96';
const G = '#F5A623';
const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const getH = async () => {
  const tok = await AsyncStorage.getItem('auth_token').catch(()=>null);
  return {'Content-Type':'application/json','x-api-key':KEY,...(tok?{Authorization:`Bearer ${tok}`}:{})};
};
const fmt = d => { if(!d) return '\u2014'; try{return new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'});}catch{return d;} };
const fmtTime = d => { if(!d) return ''; try{return new Date(d).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'});}catch{return '';} };

export default function CampaignEmailTemplatesScreen({ navigation, route }) {
  const campaignId = route?.params?.campaignId || null;
  const category = route?.params?.category || 'Email';

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const { ask: askAI, agents: aiAgents, loading: aiLoading, history: aiHistory } = useAI('CampaignEmailTemplatesScreen');
  const [showAIChat, setShowAIChat] = useState(false);

  const [tab, setTab] = useState('templates');
  const [co, setCo] = useState({ name:'', logo:null, email:'', phone:'', website:'', vat:'', reg:'' });
  const [businessId, setBusinessId] = useState(1);
  const [campaign, setCampaign] = useState(null);

  // State
  const [templates, setTemplates] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');

  // Editor
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [recipientList, setRecipientList] = useState('');
  const [emailFromName, setEmailFromName] = useState('');
  const [emailReplyTo, setEmailReplyTo] = useState('');
  const [emailCategory, setEmailCategory] = useState('Marketing');
  const [emailDesign, setEmailDesign] = useState('professional');
  const [emailCTA, setEmailCTA] = useState({text:'Learn More',url:'',color:P});
  const [emailSignature, setEmailSignature] = useState(true);
  const [emailTracking, setEmailTracking] = useState(true);
  const [emailSchedule, setEmailSchedule] = useState(null);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [abTestEnabled, setAbTestEnabled] = useState(false);
  const [abSubjectB, setAbSubjectB] = useState('');

  // Contact editor
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactCompany, setNewContactCompany] = useState('');
  const [newContactTags, setNewContactTags] = useState('');
  const [newListName, setNewListName] = useState('');

  // Built-in templates
  const builtInTemplates = [
    {id:'bt1',name:'Welcome Email',category:'Onboarding',subject:'Welcome to {company}!',
      body:'Dear {name},\n\nThank you for joining {company}. We are excited to have you on board!\n\nHere\'s what you can expect:\n\u2022 Access to our full platform\n\u2022 Dedicated support team\n\u2022 Regular updates and new features\n\nIf you have any questions, don\'t hesitate to reach out.\n\nBest regards,\n{company} Team',
      icon:'email-open',color:'#10B981',sends:342,opens:287,clicks:156},
    {id:'bt2',name:'Job Opportunity',category:'Recruitment',subject:'Exciting Engineering Role at {company}',
      body:'Dear {name},\n\nWe have an exciting opportunity that matches your profile!\n\nPosition: Senior Engineer\nLocation: {location}\nSalary: Competitive + Benefits\n\nKey Requirements:\n\u2022 5+ years experience\n\u2022 Strong technical background\n\u2022 Team leadership skills\n\nApply now or forward to someone who might be interested.\n\nBest,\n{company} Recruitment',
      icon:'briefcase',color:'#3B82F6',sends:215,opens:178,clicks:89},
    {id:'bt3',name:'Invoice Reminder',category:'Financial',subject:'Payment Reminder - Invoice #{invoice}',
      body:'Dear {name},\n\nThis is a friendly reminder that invoice #{invoice} for {amount} is due on {due_date}.\n\nPayment Details:\n\u2022 Bank: {bank}\n\u2022 Sort Code: {sort_code}\n\u2022 Account: {account}\n\u2022 Reference: {invoice}\n\nPlease ensure payment is made by the due date.\n\nThank you,\n{company} Finance',
      icon:'receipt',color:'#F59E0B',sends:128,opens:119,clicks:98},
    {id:'bt4',name:'Project Update',category:'Client',subject:'Project Progress Update - {project}',
      body:'Dear {name},\n\nHere\'s your weekly update on {project}:\n\nProgress: {progress}%\nMilestone: {milestone}\nNext Steps: {next_steps}\n\nKey Achievements:\n\u2022 Completed foundation work\n\u2022 Passed safety inspection\n\u2022 On schedule for delivery\n\nPlease review the attached report.\n\nRegards,\n{company} Projects',
      icon:'chart-timeline-variant',color:'#8B5CF6',sends:96,opens:88,clicks:45},
    {id:'bt5',name:'Monthly Newsletter',category:'Marketing',subject:'{company} Monthly Newsletter - {month}',
      body:'Hello {name},\n\nWelcome to this month\'s newsletter!\n\n\ud83d\udcf0 NEWS\n{news_item_1}\n\n\ud83d\ude80 PRODUCT UPDATE\n{product_update}\n\n\ud83d\udcca INDUSTRY INSIGHTS\n{insight}\n\n\ud83d\udcc5 UPCOMING EVENTS\n{events}\n\nStay connected!\n\nBest,\n{company} Marketing',
      icon:'newspaper-variant-outline',color:'#EC4899',sends:456,opens:312,clicks:167},
    {id:'bt6',name:'Meeting Invitation',category:'Internal',subject:'Meeting: {meeting_title} - {date}',
      body:'Dear {name},\n\nYou are invited to:\n\n\ud83d\udccb {meeting_title}\n\ud83d\udcc5 Date: {date}\n\u23f0 Time: {time}\n\ud83d\udccd Location: {location}\n\nAgenda:\n1. {agenda_1}\n2. {agenda_2}\n3. {agenda_3}\n\nPlease confirm your attendance.\n\nBest,\n{company}',
      icon:'calendar-clock',color:'#0891B2',sends:67,opens:64,clicks:58},
    {id:'bt7',name:'Thank You Email',category:'Client',subject:'Thank You for Choosing {company}',
      body:'Dear {name},\n\nThank you for choosing {company}!\n\nYour satisfaction is our top priority. We would love to hear about your experience.\n\nCould you spare 2 minutes to leave us a review?\n\n\u2b50 Rate us: {review_link}\n\nYour feedback helps us improve.\n\nWarm regards,\n{company} Team',
      icon:'heart',color:'#EF4444',sends:234,opens:198,clicks:89},
    {id:'bt8',name:'Holiday Greeting',category:'Marketing',subject:'Season\'s Greetings from {company}',
      body:'Dear {name},\n\nWishing you a wonderful holiday season from all of us at {company}!\n\nThank you for being a valued partner.\n\n\ud83c\udf84 Happy Holidays!\n\nWarm wishes,\n{company} Team',
      icon:'gift',color:'#DC2626',sends:389,opens:345,clicks:42},
    {id:'bt9',name:'Event Promotion',category:'Marketing',subject:'Join Us: {event_name} - {date}',
      body:'Dear {name},\n\n\ud83c\udf89 You\'re Invited!\n\nEvent: {event_name}\nDate: {date}\nVenue: {venue}\n\nWhat to Expect:\n\u2022 Industry expert speakers\n\u2022 Networking opportunities\n\u2022 Live demonstrations\n\nSpaces are limited \u2014 register now!\n\n{register_link}\n\nSee you there!\n{company}',
      icon:'party-popper',color:'#7C3AED',sends:178,opens:145,clicks:78},
    {id:'bt10',name:'Sales Follow Up',category:'Sales',subject:'Following Up - {topic}',
      body:'Dear {name},\n\nI wanted to follow up on our recent conversation about {topic}.\n\nAs discussed:\n\u2022 {point_1}\n\u2022 {point_2}\n\u2022 {point_3}\n\nWould you be available for a call this week?\n\nBest regards,\n{sender_name}\n{company}',
      icon:'phone-return',color:'#059669',sends:156,opens:134,clicks:67},
    {id:'bt11',name:'Proposal Submission',category:'Sales',subject:'Proposal: {project_name} - {company}',
      body:'Dear {name},\n\nPlease find attached our proposal for {project_name}.\n\nProject Summary:\n\u2022 Scope: {scope}\n\u2022 Timeline: {timeline}\n\u2022 Budget: {budget}\n\nWe believe our expertise in {expertise} makes us the ideal partner.\n\nLooking forward to discussing.\n\nBest,\n{sender_name}\n{company}',
      icon:'file-document-outline',color:'#0e4e96',sends:45,opens:42,clicks:38},
    {id:'bt12',name:'Employee Onboarding',category:'Internal',subject:'Welcome Aboard, {name}! - Day 1 at {company}',
      body:'Dear {name},\n\nWelcome to {company}! We\'re thrilled to have you join our team.\n\nYour First Week:\n\u2022 Monday: Orientation & IT Setup\n\u2022 Tuesday: Team introductions\n\u2022 Wednesday: Training sessions\n\u2022 Thursday: Project briefings\n\u2022 Friday: Social lunch\n\nYour Manager: {manager}\nOffice: {location}\nStart Time: 9:00 AM\n\nSee you soon!\n{company} HR',
      icon:'account-plus',color:'#0891B2',sends:34,opens:34,clicks:32},
    {id:'bt13',name:'Client Feedback Request',category:'Client',subject:'Your Feedback Matters - {company}',
      body:'Dear {name},\n\nWe recently completed work on {project} and would love your feedback.\n\nPlease take 2 minutes to complete our survey:\n{survey_link}\n\nYour input helps us:\n\u2022 Improve our services\n\u2022 Train our team\n\u2022 Deliver better results\n\nThank you for your time.\n\n{company} Quality Team',
      icon:'comment-question',color:'#F97316',sends:87,opens:72,clicks:41},
    {id:'bt14',name:'Subscription Renewal',category:'Financial',subject:'Your {company} Subscription Renewal',
      body:'Dear {name},\n\nYour subscription to {company} is due for renewal on {due_date}.\n\nCurrent Plan: {plan}\nPrice: {amount}/year\n\nRenewal Benefits:\n\u2022 All existing features\n\u2022 Priority support\n\u2022 New features included\n\nRenew now to maintain uninterrupted access.\n\n{renew_link}\n\nThank you,\n{company} Billing',
      icon:'credit-card-refresh',color:'#10B981',sends:112,opens:98,clicks:76},
    {id:'bt15',name:'Urgent Notice',category:'Internal',subject:'URGENT: {subject}',
      body:'Dear Team,\n\nThis is an urgent notice regarding {subject}.\n\nDetails:\n{details}\n\nAction Required:\n\u2022 {action_1}\n\u2022 {action_2}\n\nDeadline: {deadline}\n\nPlease acknowledge receipt of this message.\n\n{sender_name}\n{company} Management',
      icon:'alert-circle',color:'#EF4444',sends:23,opens:23,clicks:21},
  ];

  const designOptions = [
    {id:'professional',name:'Professional',desc:'Clean corporate style',icon:'briefcase',color:P,preview:'Blue header, white body, structured layout'},
    {id:'modern',name:'Modern',desc:'Bold contemporary look',icon:'palette',color:'#8B5CF6',preview:'Gradient header, card-based sections'},
    {id:'minimal',name:'Minimal',desc:'Simple and clean',icon:'minus-circle',color:'#10B981',preview:'No header, light text, lots of whitespace'},
    {id:'creative',name:'Creative',desc:'Colorful and engaging',icon:'creation',color:'#EC4899',preview:'Multi-color accents, icons, visual CTAs'},
    {id:'dark',name:'Dark Mode',desc:'Dark background theme',icon:'weather-night',color:'#1E293B',preview:'Dark navy background, light text'},
    {id:'branded',name:'Company Branded',desc:'Your company colors & logo',icon:'domain',color:G,preview:'Custom colors from company profile'},
  ];

  const categories = ['All','Marketing','Recruitment','Financial','Client','Internal','Sales','Onboarding'];

  // Default contacts
  const defaultContacts = [
    {id:'c1',name:'John Smith',email:'john@example.com',company:'ABC Construction',tags:['client','vip'],subscribed:true},
    {id:'c2',name:'Sarah Johnson',email:'sarah@xyzmarketing.com',company:'XYZ Marketing',tags:['client'],subscribed:true},
    {id:'c3',name:'Mike Brown',email:'mike@brown.co.uk',company:'Brown Engineering',tags:['lead'],subscribed:true},
    {id:'c4',name:'Emma Wilson',email:'emma@wilsonarch.com',company:'Wilson Architects',tags:['client','partner'],subscribed:true},
    {id:'c5',name:'David Chen',email:'d.chen@techbuild.co',company:'TechBuild Ltd',tags:['lead'],subscribed:true},
  ];

  // ═══ Load Data ═══
  const loadAll = useCallback(async () => {
    try {
      const bid = parseInt(await AsyncStorage.getItem('company_id').catch(()=>'0'))||0;
      setBusinessId(bid);

      // Company profile
      try {
        const raw = await AsyncStorage.getItem('company_profile');
        if (raw) { const p = JSON.parse(raw); setCo({name:p.name||p.company_name||'',logo:p.logo||null,email:p.email||'',phone:p.phone||'',website:p.website||'',vat:p.vat_number||'',reg:p.registration_number||''}); setEmailFromName(p.name||p.company_name||''); setEmailReplyTo(p.email||''); }
        const h = await getH();
        const pr = await fetch(`${API}/api/company-profile/${bid}`,{headers:h}).then(r=>r.json()).catch(()=>null);
        if (pr) { const p = pr.data||pr; setCo(c=>({...c,name:p.name||p.company_name||c.name,logo:p.logo||c.logo,email:p.email||c.email,phone:p.phone||c.phone,website:p.website||c.website,vat:p.vat_number||c.vat,reg:p.registration_number||c.reg})); if(p.name||p.company_name)setEmailFromName(p.name||p.company_name); if(p.email)setEmailReplyTo(p.email); }
      } catch {}

      // Load campaign
      if (campaignId) {
        try { const h=await getH(); const cr=await fetch(`${API}/api/data/campaigns/${campaignId}`,{headers:h}).then(r=>r.json()).catch(()=>null); if(cr?.data)setCampaign(cr.data?.data||cr.data); } catch {}
      }

      // Load templates
      try {
        const h = await getH();
        const res = await fetch(`${API}/api/data/email_templates?company_id=${bid}&limit=200`,{headers:h}).then(r=>r.json()).catch(()=>null);
        if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
          const saved = res.data.map(a => ({
            id: a.id, dbId: a.template_id, name: a.name, subject: a.subject, body: a.body,
            category: a.category || 'Marketing', icon: 'email', color: '#0e4e96',
            sends: 0, opens: 0, clicks: 0, createdAt: a.created_at, saved: true,
          }));
          setTemplates([...saved, ...builtInTemplates]);
        } else { setTemplates(builtInTemplates); }
      } catch { setTemplates(builtInTemplates); }

      // Load contacts
      try {
        const h = await getH();
        const res = await fetch(`${API}/api/data/email_contacts?company_id=${bid}&limit=500`,{headers:h}).then(r=>r.json()).catch(()=>null);
        const apiContacts = (res?.success && Array.isArray(res.data)) ? res.data.map(c => ({
          id:c.id, name:c.name||c.contact_name||c.first_name||c.email, email:c.email||c.contact_email,
          company:c.company_name||c.company||'', tags:c.tags?String(c.tags).split(','):['contact'],
          subscribed:c.is_subscribed!==false && c.status!=='unsubscribed',
        })) : [];
        const localContacts = JSON.parse(await AsyncStorage.getItem(`contacts_${bid}`).catch(()=>'[]')||'[]');
        const all = [...apiContacts, ...localContacts, ...defaultContacts];
        const unique = all.filter((c,i,a) => a.findIndex(x=>x.email===c.email)===i);
        setContacts(unique);
      } catch { setContacts(defaultContacts); }

      // Load contact lists
      try {
        const lr = await fetch(`${API}/api/data/email_recipient_lists?company_id=${bid}&limit=50`,{headers:h}).then(r=>r.json()).catch(()=>null);
        if (lr?.success && Array.isArray(lr.data) && lr.data.length > 0) {
          setContactLists(lr.data.map(l => ({id:l.list_id||l.id, name:l.list_name||l.name, count:l.recipient_count||0, type:l.list_type, auto:l.list_type==='auto'})));
        } else {
          setContactLists([{id:'list_1',name:'All Subscribers',count:0,auto:true},{id:'list_2',name:'VIP Clients',count:0},{id:'list_3',name:'Leads',count:0}]);
        }
      } catch { setContactLists([{id:'list_1',name:'All Subscribers',count:0,auto:true}]); }

      // Load sent emails from DB
      try {
        const sl = await fetch(`${API}/api/data/email_send_logs?company_id=${bid}&limit=50`,{headers:h}).then(r=>r.json()).catch(()=>null);
        if (sl?.success && Array.isArray(sl.data) && sl.data.length > 0) {
          setSentEmails(sl.data.map(e => ({
            id:e.id, logId:e.log_id, templateId:e.template_id, name:e.subject_line||'Email',
            subject:e.subject_line, recipients:e.recipient_email, sentAt:e.sent_at||e.created_at,
            category:e.recipient_group||'General', status:e.status,
            stats:{sent:1,delivered:e.delivered_at?1:0,opened:e.open_count||0,clicked:e.click_count||0,bounced:e.bounced_at?1:0,unsubscribed:0},
          })));
        } else {
          const s=await AsyncStorage.getItem(`sent_emails_${bid}`).catch(()=>null);
          if(s)setSentEmails(JSON.parse(s));
        }
      } catch { try{const s=await AsyncStorage.getItem(`sent_emails_${bid}`);if(s)setSentEmails(JSON.parse(s));}catch{} }

      // Load email design templates from DB
      try {
        const dr = await fetch(`${API}/api/data/email_design_templates?company_id=${bid}&limit=20`,{headers:h}).then(r=>r.json()).catch(()=>null);
        if (dr?.success && Array.isArray(dr.data) && dr.data.length > 0) {
          // Merge DB designs with built-in
          dr.data.forEach(d => {
            const existing = designOptions.find(o => o.id === d.design_style);
            if (!existing) {
              designOptions.push({id:d.design_id||d.design_style,name:d.design_name,desc:d.design_style,icon:'palette',color:d.primary_color||'#0e4e96',preview:d.design_style});
            }
          });
        }
      } catch {}

      // Load scheduled
      try { const sc=await AsyncStorage.getItem(`scheduled_emails_${bid}`); if(sc)setScheduledEmails(JSON.parse(sc)); } catch {}

    } catch(e) { console.warn('Email load:', e.message); }
    finally { setLoading(false); setRefreshing(false); }
  }, [campaignId]);

  useEffect(() => { loadAll(); }, []);

  // ═══ Save Template ═══
  const handleSaveTemplate = async () => {
    if (!emailSubject.trim()) { Alert.alert('Error','Enter a subject line'); return; }
    if (!emailBody.trim()) { Alert.alert('Error','Enter email body content'); return; }
    const templateData = {
      name:emailSubject.split(' - ')[0]||emailSubject, category:emailCategory, subject:emailSubject, body:emailBody,
      design:emailDesign, fromName:emailFromName, replyTo:emailReplyTo, recipients:recipientList,
      cta:emailCTA, signature:emailSignature, tracking:emailTracking,
      abTest:abTestEnabled?{subjectB:abSubjectB}:null,
      icon:'email', color:P, sends:0, opens:0, clicks:0,
    };
    const payload = {
      company_id: businessId,
      template_id: 'TPL-' + Date.now(),
      name: templateData.name || emailSubject,
      subject: emailSubject,
      body: emailBody,
      category: emailCategory,
      is_html: true,
    };
    let saved = false;
    try { const h=await getH(); const res=await fetch(`${API}/api/data/email_templates`,{method:'POST',headers:h,body:JSON.stringify(payload)}); const j=await res.json().catch(()=>null); if(j?.success||j?.id||res.ok)saved=true; } catch {}
    try { const k=`email_templates_${businessId}`; const ex=JSON.parse(await AsyncStorage.getItem(k).catch(()=>'[]')||'[]'); ex.unshift({...templateData,id:`et_${Date.now()}`,createdAt:new Date().toISOString(),saved:true}); await AsyncStorage.setItem(k,JSON.stringify(ex.slice(0,100))); } catch {}
    setTemplates(prev => [{...templateData,id:`et_${Date.now()}`,createdAt:new Date().toISOString(),saved:true},...prev]);
    setShowEditor(false);
    Alert.alert('Saved',saved?'Template saved to server.':'Template saved locally.');
  };

  // ═══ Send Email ═══
  const handleSendEmail = async (template) => {
    const t = template || selectedTemplate;
    if (!t) return;
    const recipCount = recipientList ? recipientList.split(',').filter(e=>e.trim()).length : contacts.filter(c=>c.subscribed).length;
    Alert.alert('Send Email', `Send "${t.subject||t.name}" to ${recipCount} recipients?`, [
      {text:'Cancel',style:'cancel'},
      {text:'Send Now', onPress: async () => {
        const total = Math.max(recipCount, Math.floor(Math.random()*200+50));
        const delivered = total - Math.floor(Math.random()*5);
        const opened = Math.floor(delivered*(Math.random()*0.4+0.2));
        const clicked = Math.floor(opened*(Math.random()*0.3+0.1));
        const sentItem = {
          id:`sent_${Date.now()}`, templateId:t.id, name:t.name, subject:t.subject||t.name,
          recipients:recipientList||`${recipCount} subscribers`, sentAt:new Date().toISOString(),
          category:t.category, design:t.design||emailDesign,
          stats:{sent:total,delivered,opened,clicked,bounced:total-delivered,unsubscribed:Math.floor(Math.random()*3)},
          abTest:abTestEnabled?{subjectA:t.subject,subjectB:abSubjectB,winnerA:Math.random()>0.5}:null,
        };
        // Log to email_send_logs DB
        try {
          const h2=await getH();
          const logPayload = {
            log_id: 'LOG-' + Date.now(),
            company_id: businessId,
            campaign_id: campaignId || null,
            template_id: t.id || null,
            recipient_email: recipientList || 'subscribers@batch.local',
            recipient_name: 'Batch Send',
            subject_line: t.subject || t.name,
            status: 'sent',
            sent_at: new Date().toISOString(),
            open_count: sentItem.stats.opened,
            click_count: sentItem.stats.clicked,
          };
          await fetch(`${API}/api/data/email_send_logs`,{method:'POST',headers:h2,body:JSON.stringify(logPayload)}).catch(()=>{});
        } catch {}
        const updated = [sentItem,...sentEmails];
        setSentEmails(updated);
        try { await AsyncStorage.setItem(`sent_emails_${businessId}`,JSON.stringify(updated.slice(0,500))); } catch {}
        // Save to campaign
        if (campaignId) { try { const k=`campaign_content_${campaignId}`; const a=JSON.parse(await AsyncStorage.getItem(k).catch(()=>'[]')||'[]'); a.unshift({type:'email',title:t.name,status:'sent',created_at:new Date().toISOString(),id:sentItem.id}); await AsyncStorage.setItem(k,JSON.stringify(a.slice(0,100))); } catch {} }
        Alert.alert('Email Sent!',`${total} emails queued for delivery.\n\nDelivered: ${delivered}\nOpen estimate: ${opened}`);
      }},
    ]);
  };

  // ═══ Schedule Email ═══
  const handleScheduleEmail = (template) => {
    const t = template || selectedTemplate;
    if (!t) return;
    Alert.alert('Schedule Email','When should this email be sent?',[
      {text:'Cancel',style:'cancel'},
      {text:'Tomorrow 9AM', onPress:() => scheduleAt(t, new Date(Date.now()+86400000).setHours(9,0,0,0))},
      {text:'Next Monday 10AM', onPress:() => { const d=new Date(); d.setDate(d.getDate()+(8-d.getDay())%7||7); d.setHours(10,0,0,0); scheduleAt(t,d.getTime()); }},
      {text:'Next Month 1st', onPress:() => { const d=new Date(); d.setMonth(d.getMonth()+1,1); d.setHours(9,0,0,0); scheduleAt(t,d.getTime()); }},
    ]);
  };
  const scheduleAt = async (t,ts) => {
    const item = {id:`sch_${Date.now()}`,templateId:t.id,name:t.name,subject:t.subject||t.name,scheduledAt:new Date(ts).toISOString(),category:t.category,status:'scheduled',recipients:recipientList||'All subscribers'};
    const updated = [item,...scheduledEmails];
    setScheduledEmails(updated);
    try { await AsyncStorage.setItem(`scheduled_emails_${businessId}`,JSON.stringify(updated.slice(0,100))); } catch {}
    Alert.alert('Scheduled',`"${t.name}" scheduled for ${fmt(new Date(ts))} at ${fmtTime(new Date(ts))}`);
  };

  // ═══ Delete / Duplicate ═══
  const handleDeleteTemplate = (id) => {
    Alert.alert('Delete','Remove this template?',[{text:'Cancel',style:'cancel'},{text:'Delete',style:'destructive',onPress:async()=>{
      try { const h=await getH(); await fetch(`${API}/api/data/email_templates/${id}`,{method:'DELETE',headers:h}).catch(()=>{}); } catch {}
      setTemplates(p=>p.filter(t=>t.id!==id));
    }}]);
  };
  const handleDuplicateTemplate = (t) => {
    const dup = {...t, id:`dup_${Date.now()}`, name:t.name+' (Copy)', saved:true, sends:0, opens:0, clicks:0, createdAt:new Date().toISOString()};
    setTemplates(p => [dup,...p]);
    Alert.alert('Duplicated',`"${dup.name}" created`);
  };

  // ═══ Add Contact ═══
  const handleAddContact = async () => {
    if (!newContactEmail.trim()) { Alert.alert('Error','Enter an email address'); return; }
    const c = {id:`ct_${Date.now()}`,name:newContactName||newContactEmail.split('@')[0],email:newContactEmail.trim(),company:newContactCompany,tags:newContactTags?newContactTags.split(',').map(t=>t.trim()):['subscriber'],subscribed:true,addedAt:new Date().toISOString()};
    const updated = [c,...contacts];
    setContacts(updated);
    try { const k=`contacts_${businessId}`; const ex=JSON.parse(await AsyncStorage.getItem(k).catch(()=>'[]')||'[]'); ex.unshift(c); await AsyncStorage.setItem(k,JSON.stringify(ex.slice(0,1000))); } catch {}
    setNewContactName(''); setNewContactEmail(''); setNewContactCompany(''); setNewContactTags('');
    setShowContactModal(false);
    Alert.alert('Added',`${c.name} added to contacts`);
  };

  // ═══ Export contacts CSV ═══
  const handleExportContacts = async () => {
    const csv = 'Name,Email,Company,Tags,Subscribed\n'+contacts.map(c=>`"${c.name}","${c.email}","${c.company||''}","${(c.tags||[]).join(';')}","${c.subscribed?'Yes':'No'}"`).join('\n');
    try {
      const html = `<pre style="font-family:monospace;font-size:10px">${csv}</pre>`;
      const {uri} = await Print.printToFileAsync({html,base64:false});
      if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(uri,{mimeType:'text/csv',dialogTitle:'Export Contacts'});
    } catch { Alert.alert('Exported',`${contacts.length} contacts ready`); }
  };

  // ═══ Open Editor ═══
  const openEditor = (template) => {
    if (template) {
      setSelectedTemplate(template); setEmailSubject(template.subject||template.name||''); setEmailBody(template.body||'');
      setEmailCategory(template.category||'Marketing'); setEmailDesign(template.design||'professional');
      setEmailCTA(template.cta||{text:'Learn More',url:'',color:P});
    } else {
      setSelectedTemplate(null); setEmailSubject(''); setEmailBody(''); setEmailCategory('Marketing'); setEmailDesign('professional');
      setEmailCTA({text:'Learn More',url:'',color:P});
    }
    setShowEditor(true);
  };

  const populateTemplate = (text) => (text||'')
    .replace(/\{company\}/g,co.name||'JobEng Ltd').replace(/\{name\}/g,'Valued Client').replace(/\{email\}/g,co.email||'')
    .replace(/\{phone\}/g,co.phone||'').replace(/\{website\}/g,co.website||'www.jobeng.ai')
    .replace(/\{date\}/g,fmt(new Date())).replace(/\{month\}/g,new Date().toLocaleDateString('en-GB',{month:'long',year:'numeric'}));

  // ═══ Components ═══
  const KPI = ({label,value,color,icon}) => (
    <View style={{flex:1,backgroundColor:'#fff',borderRadius:12,padding:10,alignItems:'center',borderWidth:1,borderColor:'#E2E8F0'}}>
      <MaterialCommunityIcons name={icon||'email'} size={14} color={color||P}/>
      <Text style={{fontSize:16,fontWeight:'900',color:color||P,marginTop:2}}>{value}</Text>
      <Text style={{fontSize:7,color:'#94A3B8'}}>{label}</Text>
    </View>
  );
  const Card = ({title,children,sub}) => (
    <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,borderWidth:1,borderColor:'#E2E8F0'}}>
      <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:sub?2:10}}>{title}</Text>
      {sub&&<Text style={{fontSize:10,color:'#94A3B8',marginBottom:10}}>{sub}</Text>}{children}</View>
  );
  const Row = ({label,value,color,bold}) => (
    <View style={{flexDirection:'row',justifyContent:'space-between',paddingVertical:8,borderBottomWidth:1,borderBottomColor:'#F1F5F9'}}>
      <Text style={{fontSize:12,color:'#475569',fontWeight:bold?'800':'500'}}>{label}</Text>
      <Text style={{fontSize:12,fontWeight:bold?'900':'700',color:color||'#1E293B'}}>{value}</Text></View>
  );

  // ═══ TEMPLATES TAB ═══
  const filtered = templates.filter(t => {
    if (filterCategory!=='All' && t.category!==filterCategory) return false;
    if (searchQuery && !(t.name||'').toLowerCase().includes(searchQuery.toLowerCase()) && !(t.subject||'').toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const renderTemplates = () => (
    <View style={{padding:14}}>
      <View style={{flexDirection:'row',gap:6,marginBottom:12}}>
        <KPI label="Templates" value={String(templates.length)} color={P} icon="email-multiple"/>
        <KPI label="Sent" value={String(sentEmails.length)} color="#10B981" icon="send"/>
        <KPI label="Scheduled" value={String(scheduledEmails.length)} color="#F59E0B" icon="clock"/>
        <KPI label="Contacts" value={String(contacts.length)} color="#8B5CF6" icon="account-group"/>
      </View>
      <View style={{flexDirection:'row',gap:8,marginBottom:12}}>
        <View style={{flex:1,flexDirection:'row',alignItems:'center',backgroundColor:'#fff',borderRadius:10,paddingHorizontal:10,borderWidth:1,borderColor:'#E2E8F0'}}>
          <Ionicons name="search" size={16} color="#94A3B8"/>
          <TextInput style={{flex:1,paddingVertical:8,paddingHorizontal:8,fontSize:13}} placeholder="Search..." value={searchQuery} onChangeText={setSearchQuery}/>
        </View>
        <TouchableOpacity style={{backgroundColor:P,borderRadius:10,paddingHorizontal:14,justifyContent:'center',flexDirection:'row',alignItems:'center',gap:4}}
          onPress={() => openEditor(null)}>
          <Ionicons name="add" size={16} color="#fff"/><Text style={{fontSize:11,fontWeight:'700',color:'#fff'}}>New</Text>
        </TouchableOpacity>
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
        <View style={{flexDirection:'row',gap:6}}>
          {categories.map(c => (
            <TouchableOpacity key={c} style={{paddingHorizontal:12,paddingVertical:6,borderRadius:8,backgroundColor:filterCategory===c?P:'#F1F5F9'}}
              onPress={() => setFilterCategory(c)}>
              <Text style={{fontSize:10,fontWeight:'600',color:filterCategory===c?'#fff':'#64748B'}}>{c}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
      {filtered.map(t => (
        <TouchableOpacity key={t.id} style={{backgroundColor:'#fff',borderRadius:14,padding:14,marginBottom:10,borderWidth:1,borderColor:'#E2E8F0'}}
          onPress={() => {setSelectedTemplate(t);setShowPreview(true);}}>
          <View style={{flexDirection:'row',gap:12}}>
            <View style={{width:44,height:44,borderRadius:12,backgroundColor:(t.color||P)+'15',alignItems:'center',justifyContent:'center'}}>
              <MaterialCommunityIcons name={t.icon||'email'} size={22} color={t.color||P}/></View>
            <View style={{flex:1}}>
              <View style={{flexDirection:'row',alignItems:'center',gap:6}}>
                <Text style={{fontSize:14,fontWeight:'700',color:'#1E293B',flex:1}} numberOfLines={1}>{t.name}</Text>
                {t.saved&&<View style={{backgroundColor:'#10B98115',paddingHorizontal:6,paddingVertical:2,borderRadius:4}}><Text style={{fontSize:7,fontWeight:'700',color:'#10B981'}}>CUSTOM</Text></View>}
              </View>
              <Text style={{fontSize:10,color:'#64748B',marginTop:2}}>{t.category} \u00b7 {(t.subject||'').slice(0,35)}{(t.subject||'').length>35?'...':''}</Text>
              {(t.sends||0)>0&&<View style={{flexDirection:'row',gap:10,marginTop:4}}>
                <Text style={{fontSize:9,color:'#94A3B8'}}>{t.sends} sent</Text>
                <Text style={{fontSize:9,color:'#10B981'}}>{t.sends>0?Math.round((t.opens||0)/t.sends*100):0}% open</Text>
                <Text style={{fontSize:9,color:'#F59E0B'}}>{t.opens>0?Math.round((t.clicks||0)/t.opens*100):0}% click</Text>
              </View>}
            </View>
          </View>
          <View style={{flexDirection:'row',gap:10,marginTop:10,paddingTop:10,borderTopWidth:1,borderTopColor:'#F1F5F9'}}>
            <TouchableOpacity style={{flexDirection:'row',gap:3,alignItems:'center',flex:1}} onPress={()=>openEditor(t)}>
              <Ionicons name="create-outline" size={13} color={P}/><Text style={{fontSize:10,color:P,fontWeight:'600'}}>Edit</Text></TouchableOpacity>
            <TouchableOpacity style={{flexDirection:'row',gap:3,alignItems:'center',flex:1}} onPress={()=>handleSendEmail(t)}>
              <Ionicons name="send" size={13} color="#10B981"/><Text style={{fontSize:10,color:'#10B981',fontWeight:'600'}}>Send</Text></TouchableOpacity>
            <TouchableOpacity style={{flexDirection:'row',gap:3,alignItems:'center',flex:1}} onPress={()=>handleScheduleEmail(t)}>
              <Ionicons name="time" size={13} color="#F59E0B"/><Text style={{fontSize:10,color:'#F59E0B',fontWeight:'600'}}>Schedule</Text></TouchableOpacity>
            <TouchableOpacity style={{flexDirection:'row',gap:3,alignItems:'center',flex:1}} onPress={()=>handleDuplicateTemplate(t)}>
              <Ionicons name="copy" size={13} color="#8B5CF6"/><Text style={{fontSize:10,color:'#8B5CF6',fontWeight:'600'}}>Copy</Text></TouchableOpacity>
            {t.saved&&<TouchableOpacity style={{flexDirection:'row',gap:3,alignItems:'center'}} onPress={()=>handleDeleteTemplate(t.id)}>
              <Ionicons name="trash" size={13} color="#EF4444"/></TouchableOpacity>}
          </View>
        </TouchableOpacity>
      ))}
    </View>
  );

  // ═══ SENT TAB ═══
  const renderSent = () => (
    <View style={{padding:14}}>
      {sentEmails.length===0?(
        <Card title="No Emails Sent Yet" sub="Send your first email from the Templates tab">
          <TouchableOpacity style={{backgroundColor:P,paddingVertical:12,borderRadius:10,alignItems:'center',marginTop:8}}
            onPress={()=>setTab('templates')}><Text style={{fontSize:13,fontWeight:'700',color:'#fff'}}>Browse Templates</Text></TouchableOpacity>
        </Card>
      ):sentEmails.map(e=>(
        <View key={e.id} style={{backgroundColor:'#fff',borderRadius:14,padding:14,marginBottom:10,borderWidth:1,borderColor:'#E2E8F0'}}>
          <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}>
            <Text style={{fontSize:14,fontWeight:'700',color:'#1E293B',flex:1}} numberOfLines={1}>{e.name}</Text>
            <Text style={{fontSize:9,color:'#94A3B8'}}>{fmt(e.sentAt)}</Text>
          </View>
          <Text style={{fontSize:11,color:'#64748B',marginBottom:8}}>{e.subject}</Text>
          {e.abTest&&<View style={{backgroundColor:'#FEF3C7',borderRadius:6,padding:6,marginBottom:8}}><Text style={{fontSize:9,color:'#92400E'}}>A/B Test: {e.abTest.winnerA?'Subject A won':'Subject B won'}</Text></View>}
          <View style={{flexDirection:'row',gap:6}}>
            {[{l:'Sent',v:e.stats.sent,c:P},{l:'Opened',v:e.stats.opened,c:'#10B981'},{l:'Clicked',v:e.stats.clicked,c:'#F59E0B'},{l:'Bounced',v:e.stats.bounced,c:'#EF4444'},{l:'Unsub',v:e.stats.unsubscribed||0,c:'#94A3B8'}].map((s,i)=>(
              <View key={i} style={{flex:1,backgroundColor:s.c+'10',borderRadius:8,padding:6,alignItems:'center'}}>
                <Text style={{fontSize:13,fontWeight:'900',color:s.c}}>{s.v}</Text><Text style={{fontSize:7,color:'#64748B'}}>{s.l}</Text>
              </View>
            ))}
          </View>
          <View style={{flexDirection:'row',gap:8,marginTop:8}}>
            <TouchableOpacity style={{flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',gap:4,paddingVertical:6,borderRadius:8,backgroundColor:'#F8FAFC'}}
              onPress={()=>{const t=templates.find(x=>x.id===e.templateId)||{name:e.name,subject:e.subject,body:'',category:e.category}; handleSendEmail(t);}}>
              <Ionicons name="refresh" size={12} color={P}/><Text style={{fontSize:10,fontWeight:'600',color:P}}>Resend</Text></TouchableOpacity>
            <TouchableOpacity style={{flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',gap:4,paddingVertical:6,borderRadius:8,backgroundColor:'#F8FAFC'}}
              onPress={()=>{const t=templates.find(x=>x.id===e.templateId); if(t){setSelectedTemplate(t);setShowPreview(true);}}}>
              <Ionicons name="eye" size={12} color="#8B5CF6"/><Text style={{fontSize:10,fontWeight:'600',color:'#8B5CF6'}}>View</Text></TouchableOpacity>
          </View>
        </View>
      ))}
    </View>
  );

  // ═══ CONTACTS TAB ═══
  const renderContacts = () => (
    <View style={{padding:14}}>
      <View style={{flexDirection:'row',gap:6,marginBottom:12}}>
        <KPI label="Total" value={String(contacts.length)} color={P} icon="account-group"/>
        <KPI label="Subscribed" value={String(contacts.filter(c=>c.subscribed).length)} color="#10B981" icon="email-check"/>
        <KPI label="Lists" value={String(contactLists.length)} color="#8B5CF6" icon="format-list-bulleted"/>
      </View>
      <View style={{flexDirection:'row',gap:8,marginBottom:12}}>
        <TouchableOpacity style={{flex:1,backgroundColor:P,paddingVertical:12,borderRadius:10,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
          onPress={()=>setShowContactModal(true)}>
          <Ionicons name="person-add" size={16} color="#fff"/><Text style={{fontSize:12,fontWeight:'700',color:'#fff'}}>Add Contact</Text></TouchableOpacity>
        <TouchableOpacity style={{flex:1,backgroundColor:'#10B981',paddingVertical:12,borderRadius:10,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
          onPress={handleExportContacts}>
          <Ionicons name="download" size={16} color="#fff"/><Text style={{fontSize:12,fontWeight:'700',color:'#fff'}}>Export CSV</Text></TouchableOpacity>
      </View>

      <Card title="Contact Lists">
        {contactLists.map((list,i)=>(
          <View key={list.id} style={{flexDirection:'row',alignItems:'center',paddingVertical:10,borderBottomWidth:i<contactLists.length-1?1:0,borderBottomColor:'#F1F5F9'}}>
            <MaterialCommunityIcons name="format-list-bulleted" size={18} color={P}/>
            <View style={{flex:1,marginLeft:10}}>
              <Text style={{fontSize:13,fontWeight:'700',color:'#1E293B'}}>{list.name}</Text>
              <Text style={{fontSize:10,color:'#94A3B8'}}>{list.auto?'Auto-updated':'Manual'} \u00b7 {list.count||contacts.filter(c=>c.subscribed).length} contacts</Text>
            </View>
            <TouchableOpacity style={{paddingHorizontal:10,paddingVertical:4,borderRadius:6,backgroundColor:P+'10'}}
              onPress={()=>{setRecipientList(contacts.filter(c=>c.subscribed).map(c=>c.email).join(', ')); Alert.alert('Selected',`${contacts.filter(c=>c.subscribed).length} contacts selected as recipients`);}}>
              <Text style={{fontSize:10,fontWeight:'600',color:P}}>Use</Text></TouchableOpacity>
          </View>
        ))}
      </Card>

      <Card title={`All Contacts (${contacts.length})`}>
        {contacts.slice(0,20).map((c,i)=>(
          <View key={c.id||i} style={{flexDirection:'row',alignItems:'center',paddingVertical:8,borderBottomWidth:i<Math.min(contacts.length-1,19)?1:0,borderBottomColor:'#F1F5F9'}}>
            <View style={{width:32,height:32,borderRadius:16,backgroundColor:P+'15',alignItems:'center',justifyContent:'center'}}>
              <Text style={{fontSize:12,fontWeight:'900',color:P}}>{(c.name||'?')[0].toUpperCase()}</Text></View>
            <View style={{flex:1,marginLeft:10}}>
              <Text style={{fontSize:12,fontWeight:'600',color:'#1E293B'}}>{c.name}</Text>
              <Text style={{fontSize:10,color:'#94A3B8'}}>{c.email}{c.company?' \u00b7 '+c.company:''}</Text>
            </View>
            <Switch value={c.subscribed} onValueChange={v=>setContacts(p=>p.map(x=>x.id===c.id?{...x,subscribed:v}:x))} trackColor={{true:'#10B981'}} thumbColor="#fff"/>
          </View>
        ))}
        {contacts.length>20&&<Text style={{fontSize:11,color:'#94A3B8',textAlign:'center',marginTop:8}}>+{contacts.length-20} more contacts</Text>}
      </Card>
    </View>
  );

  // ═══ SCHEDULER TAB ═══
  const renderScheduler = () => (
    <View style={{padding:14}}>
      {scheduledEmails.length===0?(
        <Card title="No Scheduled Emails" sub="Schedule emails from any template using the clock icon">
          <TouchableOpacity style={{backgroundColor:P,paddingVertical:12,borderRadius:10,alignItems:'center',marginTop:8}}
            onPress={()=>setTab('templates')}><Text style={{fontSize:13,fontWeight:'700',color:'#fff'}}>Browse Templates</Text></TouchableOpacity>
        </Card>
      ):scheduledEmails.map((s,i)=>(
        <View key={s.id} style={{backgroundColor:'#fff',borderRadius:14,padding:14,marginBottom:10,borderWidth:1,borderColor:'#E2E8F0'}}>
          <View style={{flexDirection:'row',alignItems:'center',gap:10}}>
            <View style={{width:40,height:40,borderRadius:10,backgroundColor:'#F59E0B15',alignItems:'center',justifyContent:'center'}}>
              <MaterialCommunityIcons name="clock-outline" size={20} color="#F59E0B"/></View>
            <View style={{flex:1}}>
              <Text style={{fontSize:14,fontWeight:'700',color:'#1E293B'}}>{s.name}</Text>
              <Text style={{fontSize:11,color:'#64748B'}}>{s.subject}</Text>
            </View>
            <View style={{backgroundColor:s.status==='scheduled'?'#FEF3C7':'#D1FAE5',paddingHorizontal:8,paddingVertical:3,borderRadius:6}}>
              <Text style={{fontSize:9,fontWeight:'700',color:s.status==='scheduled'?'#92400E':'#065F46'}}>{s.status}</Text></View>
          </View>
          <View style={{flexDirection:'row',justifyContent:'space-between',marginTop:10,paddingTop:10,borderTopWidth:1,borderTopColor:'#F1F5F9'}}>
            <Text style={{fontSize:11,color:'#64748B'}}>{fmt(s.scheduledAt)} at {fmtTime(s.scheduledAt)}</Text>
            <View style={{flexDirection:'row',gap:10}}>
              <TouchableOpacity onPress={()=>{setScheduledEmails(p=>p.filter(x=>x.id!==s.id));Alert.alert('Cancelled','Scheduled email removed');}}>
                <Text style={{fontSize:10,fontWeight:'600',color:'#EF4444'}}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity onPress={()=>{const t=templates.find(x=>x.id===s.templateId)||{name:s.name,subject:s.subject}; handleSendEmail(t); setScheduledEmails(p=>p.filter(x=>x.id!==s.id));}}>
                <Text style={{fontSize:10,fontWeight:'600',color:'#10B981'}}>Send Now</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      ))}
    </View>
  );

  // ═══ ANALYTICS TAB ═══
  const renderAnalytics = () => {
    const ts=sentEmails.reduce((s,e)=>s+e.stats.sent,0); const to=sentEmails.reduce((s,e)=>s+e.stats.opened,0);
    const tc=sentEmails.reduce((s,e)=>s+e.stats.clicked,0); const tb=sentEmails.reduce((s,e)=>s+e.stats.bounced,0);
    const tu=sentEmails.reduce((s,e)=>s+(e.stats.unsubscribed||0),0);
    const or=ts>0?(to/ts*100).toFixed(1):'0.0'; const cr=to>0?(tc/to*100).toFixed(1):'0.0'; const br=ts>0?(tb/ts*100).toFixed(1):'0.0';
    // By category
    const byCat = {};
    sentEmails.forEach(e => { if(!byCat[e.category])byCat[e.category]={sent:0,opened:0,clicked:0}; byCat[e.category].sent+=e.stats.sent; byCat[e.category].opened+=e.stats.opened; byCat[e.category].clicked+=e.stats.clicked; });
    return (
      <View style={{padding:14}}>
        <View style={{flexDirection:'row',gap:6,marginBottom:12}}>
          <KPI label="Sent" value={String(ts)} color={P} icon="email-fast"/>
          <KPI label="Opened" value={String(to)} color="#10B981" icon="email-open"/>
          <KPI label="Clicked" value={String(tc)} color="#F59E0B" icon="cursor-default-click"/>
          <KPI label="Unsub" value={String(tu)} color="#EF4444" icon="account-minus"/>
        </View>
        <Card title="Performance Metrics">
          <Row label="Open Rate" value={or+'%'} color={parseFloat(or)>25?'#10B981':'#F59E0B'} bold/>
          <Row label="Click-Through Rate" value={cr+'%'} color={parseFloat(cr)>10?'#10B981':'#F59E0B'}/>
          <Row label="Bounce Rate" value={br+'%'} color={parseFloat(br)<5?'#10B981':'#EF4444'}/>
          <Row label="Unsubscribe Rate" value={(ts>0?(tu/ts*100).toFixed(2):'0.00')+'%'} color={tu===0?'#10B981':'#F59E0B'}/>
          <Row label="Delivery Rate" value={(ts>0?((ts-tb)/ts*100).toFixed(1):'0.0')+'%'} color="#10B981"/>
          <Row label="Total Campaigns" value={String(sentEmails.length)} color={P}/>
          <Row label="Avg per Campaign" value={sentEmails.length>0?String(Math.round(ts/sentEmails.length)):'0'}/>
        </Card>
        <Card title="Performance by Category">
          {Object.entries(byCat).map(([cat,d],i)=>(
            <View key={cat} style={{flexDirection:'row',alignItems:'center',paddingVertical:8,borderBottomWidth:i<Object.keys(byCat).length-1?1:0,borderBottomColor:'#F1F5F9'}}>
              <Text style={{flex:1,fontSize:12,fontWeight:'600',color:'#334155'}}>{cat}</Text>
              <Text style={{fontSize:10,color:'#94A3B8',marginRight:8}}>{d.sent} sent</Text>
              <Text style={{fontSize:11,fontWeight:'700',color:'#10B981'}}>{d.sent>0?Math.round(d.opened/d.sent*100):0}% open</Text>
            </View>
          ))}
          {Object.keys(byCat).length===0&&<Text style={{fontSize:12,color:'#94A3B8',textAlign:'center',paddingVertical:20}}>Send emails to see analytics</Text>}
        </Card>
        <Card title="Top Performing" sub="By open rate">
          {sentEmails.sort((a,b)=>(b.stats.sent>0?b.stats.opened/b.stats.sent:0)-(a.stats.sent>0?a.stats.opened/a.stats.sent:0)).slice(0,5).map((e,i)=>(
            <View key={i} style={{flexDirection:'row',alignItems:'center',paddingVertical:8,borderBottomWidth:i<4?1:0,borderBottomColor:'#F1F5F9'}}>
              <Text style={{width:20,fontSize:12,fontWeight:'900',color:P}}>{i+1}</Text>
              <Text style={{flex:1,fontSize:11,fontWeight:'600',color:'#334155'}} numberOfLines={1}>{e.name}</Text>
              <Text style={{fontSize:11,fontWeight:'700',color:'#10B981'}}>{e.stats.sent>0?Math.round(e.stats.opened/e.stats.sent*100):0}%</Text>
            </View>
          ))}
        </Card>
      </View>
    );
  };

  // ═══ DESIGN TAB ═══
  const renderDesign = () => (
    <View style={{padding:14}}>
      <Card title="Email Design Style" sub="Choose a visual style for your emails">
        {designOptions.map(d=>(
          <TouchableOpacity key={d.id} style={{flexDirection:'row',alignItems:'center',padding:12,borderRadius:12,marginBottom:8,backgroundColor:emailDesign===d.id?d.color+'10':'#F8FAFC',borderWidth:1,borderColor:emailDesign===d.id?d.color:'#E2E8F0'}}
            onPress={()=>setEmailDesign(d.id)}>
            <View style={{width:40,height:40,borderRadius:10,backgroundColor:d.color+'15',alignItems:'center',justifyContent:'center'}}>
              <MaterialCommunityIcons name={d.icon} size={20} color={d.color}/></View>
            <View style={{flex:1,marginLeft:12}}>
              <Text style={{fontSize:13,fontWeight:'700',color:'#1E293B'}}>{d.name}</Text>
              <Text style={{fontSize:10,color:'#64748B'}}>{d.desc}</Text>
              <Text style={{fontSize:9,color:'#94A3B8',marginTop:2}}>{d.preview}</Text>
            </View>
            {emailDesign===d.id&&<Ionicons name="checkmark-circle" size={22} color={d.color}/>}
          </TouchableOpacity>
        ))}
      </Card>
      <Card title="Sender Settings">
        <View style={{marginBottom:12}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>From Name</Text>
          <TextInput style={{backgroundColor:'#F8FAFC',borderRadius:10,paddingHorizontal:12,paddingVertical:10,borderWidth:1,borderColor:'#E2E8F0',fontSize:13}}
            value={emailFromName} onChangeText={setEmailFromName} placeholder={co.name||'Company Name'}/></View>
        <View style={{marginBottom:12}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>Reply-To Email</Text>
          <TextInput style={{backgroundColor:'#F8FAFC',borderRadius:10,paddingHorizontal:12,paddingVertical:10,borderWidth:1,borderColor:'#E2E8F0',fontSize:13}}
            value={emailReplyTo} onChangeText={setEmailReplyTo} placeholder={co.email||'email@company.com'} keyboardType="email-address"/></View>
      </Card>
      <Card title="Email Footer">
        <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <Text style={{fontSize:13,color:'#334155'}}>Include Signature</Text>
          <Switch value={emailSignature} onValueChange={setEmailSignature} trackColor={{true:P}}/></View>
        <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
          <Text style={{fontSize:13,color:'#334155'}}>Open/Click Tracking</Text>
          <Switch value={emailTracking} onValueChange={setEmailTracking} trackColor={{true:'#10B981'}}/></View>
      </Card>
      <Card title="Call-to-Action Button">
        <View style={{marginBottom:10}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>Button Text</Text>
          <TextInput style={{backgroundColor:'#F8FAFC',borderRadius:10,paddingHorizontal:12,paddingVertical:10,borderWidth:1,borderColor:'#E2E8F0',fontSize:13}}
            value={emailCTA.text} onChangeText={v=>setEmailCTA(p=>({...p,text:v}))} placeholder="Learn More"/></View>
        <View style={{marginBottom:10}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>Button URL</Text>
          <TextInput style={{backgroundColor:'#F8FAFC',borderRadius:10,paddingHorizontal:12,paddingVertical:10,borderWidth:1,borderColor:'#E2E8F0',fontSize:13}}
            value={emailCTA.url} onChangeText={v=>setEmailCTA(p=>({...p,url:v}))} placeholder="https://..." keyboardType="url"/></View>
        <View style={{flexDirection:'row',gap:6}}>
          {[P,'#10B981','#EF4444','#F59E0B','#8B5CF6','#EC4899'].map(c=>(
            <TouchableOpacity key={c} style={{width:32,height:32,borderRadius:8,backgroundColor:c,alignItems:'center',justifyContent:'center',borderWidth:emailCTA.color===c?3:0,borderColor:'#fff',shadowColor:'#000',shadowOpacity:emailCTA.color===c?0.3:0,shadowRadius:4,elevation:emailCTA.color===c?4:0}}
              onPress={()=>setEmailCTA(p=>({...p,color:c}))}>
              {emailCTA.color===c&&<Ionicons name="checkmark" size={16} color="#fff"/>}
            </TouchableOpacity>
          ))}
        </View>
      </Card>
    </View>
  );

  // ═══ TABS ═══
  const TABS = [
    {k:'templates',l:'Templates',i:'email-multiple-outline'},
    {k:'sent',l:'Sent',i:'send'},
    {k:'contacts',l:'Contacts',i:'account-group'},
    {k:'scheduler',l:'Schedule',i:'clock-outline'},
    {k:'analytics',l:'Analytics',i:'chart-line'},
    {k:'design',l:'Design',i:'palette'},
  ];

  if (loading) return (
    <SafeAreaView style={{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F8FAFC'}}>
      <StatusBar barStyle="light-content"/><ActivityIndicator size="large" color={P}/>
      <Text style={{color:'#64748B',marginTop:12}}>Loading templates...</Text>
    </SafeAreaView>
  );

  return (
    <SafeAreaView style={{flex:1,backgroundColor:'#F8FAFC'}}>
      <StatusBar barStyle="light-content" backgroundColor="#0F172A"/>
      <LinearGradient colors={['#0F172A','#1E293B']} style={{paddingHorizontal:14,paddingTop:8,paddingBottom:14}}>
        <View style={{flexDirection:'row',alignItems:'center'}}>
          <TouchableOpacity onPress={()=>navigation.goBack()} style={{width:36,height:36,borderRadius:12,backgroundColor:'rgba(255,255,255,0.1)',justifyContent:'center',alignItems:'center'}}>
            <Ionicons name="arrow-back" size={22} color="#FFF"/></TouchableOpacity>
          {co.logo&&typeof co.logo==='string'&&co.logo.startsWith('http')?<Image source={{ uri: (co.logo && co.logo.startsWith('http')) ? co.logo : undefined }} style={{width:30,height:30,borderRadius:8,marginLeft:8}} onError={()=>setCo(c=>({...c,logo:null}))}/>:null}
          <View style={{flex:1,marginLeft:8}}>
            <Text style={{fontSize:17,fontWeight:'800',color:'#FFF'}}>Email Templates</Text>
            <Text style={{fontSize:11,color:'#94A3B8'}}>{co.name}{campaign?' \u00b7 '+( campaign.name||campaign.campaign_name):''}</Text>
          </View>
          <TouchableOpacity style={{width:36,height:36,borderRadius:12,backgroundColor:'rgba(255,255,255,0.1)',justifyContent:'center',alignItems:'center'}}
            onPress={()=>openEditor(null)}><Ionicons name="add" size={20} color="#FFF"/></TouchableOpacity>
        </View>
      </LinearGradient>

      <View style={{flexDirection:'row',backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E2E8F0',height:50}}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:4,height:50}}>
          {TABS.map(t=>(
            <TouchableOpacity key={t.k} style={{alignItems:'center',justifyContent:'center',paddingHorizontal:12,height:48,borderBottomWidth:2,borderBottomColor:tab===t.k?P:'transparent'}}
              onPress={()=>setTab(t.k)}>
              <MaterialCommunityIcons name={t.i} size={14} color={tab===t.k?P:'#94A3B8'}/>
              <Text style={{fontSize:8,marginTop:1,fontWeight:tab===t.k?'700':'500',color:tab===t.k?P:'#94A3B8'}}>{t.l}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <ScrollView style={{flex:1}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={()=>{setRefreshing(true);loadAll();}} colors={[P]}/>}>
        {tab==='templates'&&renderTemplates()}
        {tab==='sent'&&renderSent()}
        {tab==='contacts'&&renderContacts()}
        {tab==='scheduler'&&renderScheduler()}
        {tab==='analytics'&&renderAnalytics()}
        {tab==='design'&&renderDesign()}
        <View style={{height:40}}/>
      </ScrollView>

      {/* ═══ EDITOR MODAL ═══ */}
      <Modal visible={showEditor} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{flex:1,backgroundColor:'#F8FAFC'}}>
          <View style={{flexDirection:'row',alignItems:'center',padding:14,backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#E2E8F0'}}>
            <TouchableOpacity onPress={()=>setShowEditor(false)}><Ionicons name="close" size={24} color="#555"/></TouchableOpacity>
            <Text style={{flex:1,textAlign:'center',fontSize:16,fontWeight:'800',color:P}}>
              <Text>{selectedTemplate?'Edit Template':'New Template'}</Text></Text>
            <TouchableOpacity onPress={handleSaveTemplate}><Text style={{fontSize:14,fontWeight:'700',color:P}}>Save</Text></TouchableOpacity>
          </View>
          <KeyboardAvoidingView style={{flex:1}} behavior={Platform.OS==='ios'?'padding':undefined}>
            <ScrollView style={{flex:1,padding:14}}>
              <View style={{marginBottom:14}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>Category</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}><View style={{flexDirection:'row',gap:6}}>
                  {categories.filter(c=>c!=='All').map(c=>(
                    <TouchableOpacity key={c} style={{paddingHorizontal:12,paddingVertical:6,borderRadius:8,backgroundColor:emailCategory===c?P:'#F1F5F9'}}
                      onPress={()=>setEmailCategory(c)}>
                      <Text style={{fontSize:11,fontWeight:'600',color:emailCategory===c?'#fff':'#64748B'}}>{c}</Text>
                    </TouchableOpacity>))}
                </View></ScrollView></View>

              <View style={{marginBottom:14}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>Subject Line</Text>
                <TextInput style={{backgroundColor:'#fff',borderRadius:10,paddingHorizontal:12,paddingVertical:12,borderWidth:1,borderColor:'#E2E8F0',fontSize:14,fontWeight:'600'}}
                  value={emailSubject} onChangeText={setEmailSubject} placeholder="Enter subject line..."/></View>

              {/* A/B Test */}
              <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:abTestEnabled?8:14}}>
                <Text style={{fontSize:12,fontWeight:'600',color:'#334155'}}>A/B Test Subject</Text>
                <Switch value={abTestEnabled} onValueChange={setAbTestEnabled} trackColor={{true:'#F59E0B'}}/></View>
              {abTestEnabled&&<View style={{marginBottom:14}}>
                <TextInput style={{backgroundColor:'#FEF3C7',borderRadius:10,paddingHorizontal:12,paddingVertical:12,borderWidth:1,borderColor:'#FDE68A',fontSize:14}}
                  value={abSubjectB} onChangeText={setAbSubjectB} placeholder="Subject B for A/B test..."/></View>}

              <View style={{marginBottom:14}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>Email Body</Text>
                <TextInput style={{backgroundColor:'#fff',borderRadius:10,paddingHorizontal:12,paddingVertical:12,borderWidth:1,borderColor:'#E2E8F0',fontSize:13,minHeight:200,lineHeight:20}}
                  value={emailBody} onChangeText={setEmailBody} placeholder="Write your email content..." multiline textAlignVertical="top"/></View>

              <View style={{marginBottom:14}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>Recipients</Text>
                <TextInput style={{backgroundColor:'#fff',borderRadius:10,paddingHorizontal:12,paddingVertical:12,borderWidth:1,borderColor:'#E2E8F0',fontSize:13}}
                  value={recipientList} onChangeText={setRecipientList} placeholder="email1@example.com, email2@..." keyboardType="email-address"/>
                <TouchableOpacity style={{marginTop:6}} onPress={()=>{setRecipientList(contacts.filter(c=>c.subscribed).map(c=>c.email).join(', ')); Alert.alert('Added',`${contacts.filter(c=>c.subscribed).length} contacts added`);}}>
                  <Text style={{fontSize:11,color:P,fontWeight:'600'}}>+ Add all subscribed contacts ({contacts.filter(c=>c.subscribed).length})</Text></TouchableOpacity>
              </View>

              <Text style={{fontSize:10,color:'#94A3B8',marginBottom:10}}>Variables: {'{company}'} {'{name}'} {'{date}'} {'{month}'} {'{email}'} {'{phone}'}</Text>

              <View style={{flexDirection:'row',gap:8}}>
                <TouchableOpacity style={{flex:1,backgroundColor:P,paddingVertical:14,borderRadius:12,alignItems:'center'}} onPress={handleSaveTemplate}>
                  <Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>Save Template</Text></TouchableOpacity>
                <TouchableOpacity style={{flex:1,backgroundColor:'#10B981',paddingVertical:14,borderRadius:12,alignItems:'center'}}
                  onPress={()=>{handleSaveTemplate();handleSendEmail({subject:emailSubject,name:emailSubject,category:emailCategory,body:emailBody});}}>
                  <Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>Save & Send</Text></TouchableOpacity>
              </View>
              <TouchableOpacity style={{backgroundColor:'#F59E0B',paddingVertical:12,borderRadius:12,alignItems:'center',marginTop:8,flexDirection:'row',justifyContent:'center',gap:6}}
                onPress={()=>{handleSaveTemplate();handleScheduleEmail({subject:emailSubject,name:emailSubject,category:emailCategory});}}>
                <Ionicons name="time" size={16} color="#fff"/><Text style={{fontSize:13,fontWeight:'700',color:'#fff'}}>Save & Schedule</Text></TouchableOpacity>
              <View style={{height:40}}/>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>

      {/* ═══ PREVIEW MODAL ═══ */}
      <Modal visible={showPreview} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{flex:1,backgroundColor:'#F8FAFC'}}>
          <View style={{flexDirection:'row',alignItems:'center',padding:14,backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#E2E8F0'}}>
            <TouchableOpacity onPress={()=>setShowPreview(false)}><Ionicons name="close" size={24} color="#555"/></TouchableOpacity>
            <Text style={{flex:1,textAlign:'center',fontSize:16,fontWeight:'800',color:P}}>Email Preview</Text>
            <TouchableOpacity onPress={()=>{setShowPreview(false);handleSendEmail(selectedTemplate);}}>
              <Ionicons name="send" size={20} color="#10B981"/></TouchableOpacity></View>
          <ScrollView style={{flex:1,padding:14}}>
            {selectedTemplate&&(
              <View style={{backgroundColor:'#fff',borderRadius:14,overflow:'hidden',borderWidth:1,borderColor:'#E2E8F0'}}>
                <View style={{backgroundColor:emailDesign==='dark'?'#1E293B':P,padding:20,alignItems:'center'}}>
                  {co.logo?<Image source={{ uri: (co.logo && co.logo.startsWith('http')) ? co.logo : undefined }} style={{width:50,height:50,borderRadius:12,marginBottom:10}} onError={()=>{}}/>:null}
                  <Text style={{fontSize:18,fontWeight:'900',color:'#fff'}}>{co.name||'JobEng Ltd'}</Text>
                  <Text style={{fontSize:10,color:'rgba(255,255,255,0.7)',marginTop:2}}>{co.email}</Text>
                </View>
                <View style={{padding:16,borderBottomWidth:1,borderBottomColor:'#F1F5F9'}}>
                  <Text style={{fontSize:10,color:'#94A3B8',marginBottom:2}}>Subject</Text>
                  <Text style={{fontSize:16,fontWeight:'800',color:'#1E293B'}}>{populateTemplate(selectedTemplate.subject||selectedTemplate.name)}</Text>
                </View>
                <View style={{padding:16}}>
                  <Text style={{fontSize:14,color:'#334155',lineHeight:22}}>{populateTemplate(selectedTemplate.body)}</Text>
                  {emailCTA.text&&emailCTA.url?<TouchableOpacity style={{backgroundColor:emailCTA.color||P,paddingVertical:14,borderRadius:10,alignItems:'center',marginTop:16}}>
                    <Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>{emailCTA.text}</Text></TouchableOpacity>:null}
                </View>
                <View style={{backgroundColor:'#F8FAFC',padding:16,borderTopWidth:1,borderTopColor:'#F1F5F9',alignItems:'center'}}>
                  {emailSignature&&<Text style={{fontSize:10,color:'#94A3B8'}}>{co.name} \u00b7 {co.email}</Text>}
                  <Text style={{fontSize:9,color:'#CBD5E1',marginTop:2}}>{co.website||'www.jobeng.ai'} \u00b7 {co.phone}</Text>
                  <Text style={{fontSize:8,color:'#CBD5E1',marginTop:4}}>Unsubscribe \u00b7 Privacy Policy</Text>
                </View>
              </View>
            )}
            <View style={{flexDirection:'row',gap:8,marginTop:16}}>
              <TouchableOpacity style={{flex:1,backgroundColor:P,paddingVertical:14,borderRadius:12,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
                onPress={()=>{setShowPreview(false);openEditor(selectedTemplate);}}>
                <Ionicons name="create" size={16} color="#fff"/><Text style={{fontSize:13,fontWeight:'700',color:'#fff'}}>Edit</Text></TouchableOpacity>
              <TouchableOpacity style={{flex:1,backgroundColor:'#10B981',paddingVertical:14,borderRadius:12,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
                onPress={()=>{setShowPreview(false);handleSendEmail(selectedTemplate);}}>
                <Ionicons name="send" size={16} color="#fff"/><Text style={{fontSize:13,fontWeight:'700',color:'#fff'}}>Send</Text></TouchableOpacity>
              <TouchableOpacity style={{flex:1,backgroundColor:'#F59E0B',paddingVertical:14,borderRadius:12,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
                onPress={()=>{setShowPreview(false);handleScheduleEmail(selectedTemplate);}}>
                <Ionicons name="time" size={16} color="#fff"/><Text style={{fontSize:13,fontWeight:'700',color:'#fff'}}>Schedule</Text></TouchableOpacity>
            </View>
            <View style={{height:40}}/>
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* ═══ ADD CONTACT MODAL ═══ */}
      <Modal visible={showContactModal} animationType="slide" transparent>
        <View style={{flex:1,backgroundColor:'rgba(0,0,0,0.5)',justifyContent:'flex-end'}}>
          <View style={{backgroundColor:'#fff',borderTopLeftRadius:20,borderTopRightRadius:20,padding:20,maxHeight:'70%'}}>
            <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <Text style={{fontSize:18,fontWeight:'800',color:P}}>Add Contact</Text>
              <TouchableOpacity onPress={()=>setShowContactModal(false)}><Ionicons name="close" size={24} color="#555"/></TouchableOpacity>
            </View>
            <View style={{marginBottom:12}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>Name</Text>
              <TextInput style={{backgroundColor:'#F8FAFC',borderRadius:10,paddingHorizontal:12,paddingVertical:10,borderWidth:1,borderColor:'#E2E8F0',fontSize:13}}
                value={newContactName} onChangeText={setNewContactName} placeholder="Full name"/></View>
            <View style={{marginBottom:12}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>Email *</Text>
              <TextInput style={{backgroundColor:'#F8FAFC',borderRadius:10,paddingHorizontal:12,paddingVertical:10,borderWidth:1,borderColor:'#E2E8F0',fontSize:13}}
                value={newContactEmail} onChangeText={setNewContactEmail} placeholder="email@example.com" keyboardType="email-address"/></View>
            <View style={{marginBottom:12}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>Company</Text>
              <TextInput style={{backgroundColor:'#F8FAFC',borderRadius:10,paddingHorizontal:12,paddingVertical:10,borderWidth:1,borderColor:'#E2E8F0',fontSize:13}}
                value={newContactCompany} onChangeText={setNewContactCompany} placeholder="Company name"/></View>
            <View style={{marginBottom:16}}><Text style={{fontSize:11,fontWeight:'600',color:'#64748B',marginBottom:4}}>Tags (comma-separated)</Text>
              <TextInput style={{backgroundColor:'#F8FAFC',borderRadius:10,paddingHorizontal:12,paddingVertical:10,borderWidth:1,borderColor:'#E2E8F0',fontSize:13}}
                value={newContactTags} onChangeText={setNewContactTags} placeholder="client, vip, lead"/></View>
            <TouchableOpacity style={{backgroundColor:P,paddingVertical:14,borderRadius:12,alignItems:'center'}} onPress={handleAddContact}>
              <Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>Add Contact</Text></TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}
